import { useMemo, useRef } from "react";
import type { TimeRange } from "@heraion/shared";

import { createSessionResultsCache } from "./sessionResultsCache";

type PeriodCache = ReturnType<typeof createSessionResultsCache>;

// PG-64: one session-results snapshot per report period. The cache module
// holds one snapshot, so each period gets its own. The last `max` periods
// stay in memory, least recently used out first: going back to one of them
// does not refetch.
export function usePeriodCaches(range: TimeRange, max: number): PeriodCache {
  const kept = useRef(new Map<string, PeriodCache>());
  const key = `${range.fromMs}:${range.toMs}`;
  return useMemo(() => {
    const map = kept.current;
    const cache = map.get(key) ?? createSessionResultsCache();
    // Re-inserting moves the key to the end: Map order is the recency order.
    map.delete(key);
    map.set(key, cache);
    while (map.size > max) {
      const oldest = map.keys().next().value;
      if (oldest === undefined) break;
      map.delete(oldest);
    }
    return cache;
  }, [key, max]);
}
